// Side-effect imports: <std-accordion> must be registered before this
// hydrates one, and <std-state-dot> before a row builds an error mark. Both
// classes are otherwise referenced only for their statics/types.
import "@tento-ui/components/accordion";
import "../turn/state-dot";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { Typography } from "@tento-ui/ui/typography";
import { Accordion } from "@tento-ui/components/accordion";
import { StateDot } from "../turn/state-dot";

/** `std-dsh-turn-row` — the dsh skin's disclosure row, and the only thing in
 * this skin that draws a row at all.
 *
 * It is SKIN-PRIVATE: it answers no slot, `skin.ts` never names it, and its
 * props are this file's to change. Thought, tool, ask, plan and attachment
 * are all the same object in dsh's transcript — a 24px line that says what
 * kind of thing happened, what it happened to, and opens — so they are one
 * primitive here rather than five files that agree on a number today.
 *
 * ## The grammar, left to right
 *
 *     [lead 16px] label · subject ..................... suffix
 *
 * A leading box, a LABEL, a 2px dot, a SUBJECT that takes the rest of the
 * line and ellipsises, and an optional trailing fact. Label and subject are
 * the SAME size and differ by one gray step — `--text-sec` against
 * `--text-ter`. That is the whole typographic system of the row: no bold, no
 * caps, no italic, no second face. dsh's transcript reads as one continuous
 * gray column because every row spends its contrast on the same two steps,
 * and a row that reaches for a third is the row that breaks the column.
 *
 * ## The leading box holds two marks and shows one
 *
 * A kind glyph at rest, a chevron on hover. They occupy the same 16px cell
 * and cross-fade in 100ms, so the row gains an affordance under the cursor
 * without gaining a column — which is what a chevron parked beside every
 * glyph would cost, on every row, forever. Open, the chevron simply stays.
 *
 * The chevron is `Accordion.chevronIcon()` and not a text glyph. A glyph's
 * ink sits where its typeface puts it inside a line box, so centring the box
 * does not centre the mark; the accordion learned that over three rounds of
 * review and exported the drawn one so nobody has to learn it again.
 *
 * ## Two states replace the glyph, two do not
 *
 * `error` and `warn` swap the glyph for `std-state-dot` in the matching
 * state — a verdict is the loudest thing the row has to say, and it says it
 * where the reader is already looking. `running` does NOT: it keeps the
 * glyph and lights the whole row with a glare band, because "still going" is
 * a fact about the row rather than about its gutter, and a mark that changes
 * shape mid-flight makes a column of rows shimmer.
 *
 * ## It is an accordion, for the reason `turn/` is
 *
 * The native skin's rows converged on `std-accordion` after collecting four
 * alignment verdicts for hand-rolling a disclosure. This skin starts there.
 * What differs is the chrome: the accordion's own icon column is zeroed
 * (`--accordion-icon-size: 0px`) because this row draws its own leading box,
 * and the header padding goes to zero because a transcript row is a denser
 * object than a page section's header.
 *
 * A row with no body passes no content, and the accordion reads that as
 * "nothing to disclose": no chevron, no click, no focus stop, no hover
 * light. That is the `body: null` case, and it costs this file nothing. */
@component({ name: "std-dsh-turn-row" })
export class DshTurnRow extends LonaWebComponent {
  private $row: Accordion = this.hydrate("row");

  static makeWith(props: DshTurnRow.Props): DshTurnRow {
    return (DshTurnRow.make() as DshTurnRow).bind(props);
  }

  /** Idempotent by construction: every field is written on every bind, and
   * the accordion rebuilds its whole tree from `items`, so a pooled row
   * arriving from a failed tool call renders an idle thought with nothing
   * left over. Nothing is accumulated and nothing is toggled. */
  bind(props: DshTurnRow.Props): this {
    const state = props.state ?? "idle";
    this.setAttribute("state", state);
    const $body = props.body ?? null;
    if ($body != null) DshTurnRow.layoutBody($body, props.bodyIndent ?? "prose");
    const $header = DshTurnRow.buildHeader(props, state, $body != null);
    // A row with no body normally refuses the pointer — the accordion's
    // inert header sets `cursor: default`, which is right for a row that is
    // only information. A row whose whole box IS a control (an attachment
    // that opens) still has no disclosure, so the accordion cannot know;
    // this header element sits inside the inert wrapper, and a cursor set
    // on it wins under the pointer wherever the row's ink is. Written on
    // every bind, cleared on every bind — pooled rows carry nothing over.
    $header.style.cursor = (props.interactive ?? false) && $body == null
      ? "pointer"
      : "";
    void this.$row.bind({
      items: [{
        header: $header,
        content: $body ?? "",
      }],
      expandedIndexes: $body != null && (props.expanded ?? false) ? [0] : [],
    });
    return this;
  }

  /** The body's geometry, written onto the caller's element rather than into
   * `--accordion-content-padding`.
   *
   * An ELEMENT handed to the accordion as content BECOMES the padded box —
   * `renderContent` puts `.accordion-content-custom` on the element itself
   * rather than wrapping it — so the knob and this element's own padding are
   * the same declaration, and inline wins. The knob is therefore zeroed in
   * `$styles` and the indent lives here.
   *
   * Two indents and not one number. A prose body is a continuation of the
   * row's sentence and starts where the row's own words start (the 16px lead
   * box plus its 6px gap); a CARD is an object with its own border, and an
   * object indented to a text column reads as hanging off the row rather
   * than under it. */
  private static layoutBody($body: HTMLElement, indent: DshTurnRow.BodyIndent): void {
    const left = indent === "card" ? "4px" : "22px";
    Object.assign($body.style, { margin: `2px 0 6px ${left}`, padding: "0", minWidth: "0" });
  }

  /** The row's one line, built as an element because it goes INTO the
   * accordion's shadow root, where a stylesheet declared here cannot reach
   * it. Typography's sheet IS in every shadow root, so the ramp classes
   * resolve and only the values travel — and the rules that an inline style
   * cannot express (`:hover`, `[aria-expanded]`) ride along as a `<style>`
   * inside the header, scoped to the tree it lands in. */
  private static buildHeader(
    props: DshTurnRow.Props,
    state: DshTurnRow.State,
    discloses: boolean,
  ): HTMLElement {
    const $header = document.createElement("div");
    $header.className = discloses && !(props.keepSubjectWhenOpen ?? false)
      ? "dsh-row dsh-hide-open"
      : "dsh-row";

    const $style = document.createElement("style");
    $style.textContent = DshTurnRow.ROW_STYLE;

    const $label = Typography.small(props.label);
    $label.className = "small dsh-label";

    const $sep = document.createElement("span");
    $sep.className = "dsh-sep";
    $sep.setAttribute("aria-hidden", "true");

    const $subject = typeof props.subject === "string"
      ? Typography.small(props.subject)
      : props.subject;
    $subject.classList.add("dsh-subject");

    $header.append(
      $style,
      DshTurnRow.buildLead(props.kind, state, discloses),
      $label,
      $sep,
      $subject,
    );

    const suffix = props.suffix ?? "";
    if (suffix !== "") {
      const $suffix = Typography.small(suffix);
      $suffix.className = "small dsh-suffix";
      $header.append($suffix);
    }
    return $header;
  }

  /** The leading box: the kind's glyph, or a verdict, and the chevron that
   * fades in over it.
   *
   * `--state-dot-size: 10px` rather than the dot's own 14px. The 14 is the
   * size at which the `ongoing` matrix lands on whole device pixels, and
   * neither state used here draws that matrix — `error` and `warn` are the
   * circle face, which has no grid to miss. 10px is the reviewed weight
   * against this row's words, and it is what keeps the lead box the same
   * 16px cell whether it holds a character or a mark. */
  private static buildLead(
    kind: DshTurnRow.Kind,
    state: DshTurnRow.State,
    discloses: boolean,
  ): HTMLElement {
    const $lead = document.createElement("span");
    $lead.className = discloses ? "dsh-lead dsh-has-chevron" : "dsh-lead";

    if (state === "error" || state === "warn") {
      const $dot = StateDot.makeWith({ state });
      $dot.classList.add("dsh-glyph", "dsh-mark");
      $dot.style.setProperty("--state-dot-size", "10px");
      $lead.append($dot);
    } else {
      const $glyph = Typography.small(DshTurnRow.GLYPH[kind]);
      $glyph.className = "small dsh-glyph";
      $lead.append($glyph);
    }

    if (discloses) {
      const $chevron = document.createElement("span");
      $chevron.className = "dsh-chevron";
      $chevron.setAttribute("aria-hidden", "true");
      $chevron.append(Accordion.chevronIcon());
      $lead.append($chevron);
    }
    return $lead;
  }

  /** The header's rules, carried into the accordion's shadow root by the
   * `<style>` `buildHeader` appends.
   *
   * An array of quoted lines rather than a tagged template, and this is not
   * a style preference: a stray backtick anywhere inside a css`` literal —
   * in a comment, in a selector, in a content string — terminates the
   * literal and ships a blank page. The native skin's diff rules dodge it
   * the same way, and this text is edited by hand more often than that one.
   *
   * The two marks share one cell and cross-fade on OPACITY. Nothing in this
   * block changes a box: a transition on width or display would relayout the
   * row under the cursor, which is the one thing a dense column cannot do.
   *
   * Every selector that touches a Typography element is DESCENDED from
   * another class, and that is load-bearing rather than tidy. Component
   * stylesheets are pushed onto `adoptedStyleSheets`, and adopted sheets
   * come AFTER tree-order `<style>` elements in the cascade — so a bare
   * `.dsh-label { margin: 0 }` in here LOSES the tie to Typography's own
   * `.small` rule, and the negative cap-height trim it sets survives. A
   * trimmed box is right for stacked prose and wrong for a flex item: flex
   * lays out MARGIN boxes, so an untrimmed run makes this row participate as
   * something other than its own ink. Two classes beat one. */
  private static readonly ROW_STYLE = [
    ".dsh-row {",
    "  display: flex;",
    "  align-items: center;",
    "  flex: 1;",
    "  min-width: 0;",
    "  min-height: 24px;",
    "}",
    ".dsh-lead {",
    "  position: relative;",
    "  flex: none;",
    "  width: 16px;",
    "  height: 16px;",
    "  margin-right: 6px;",
    "}",
    ".dsh-lead .dsh-glyph, .dsh-lead .dsh-chevron {",
    "  position: absolute;",
    "  inset: 0;",
    "  display: flex;",
    "  align-items: center;",
    "  justify-content: center;",
    "  margin: 0;",
    "  min-height: 0;",
    "  color: var(--text-ter, #8b93a7);",
    "  transition: opacity 100ms ease;",
    "}",
    /* The verdict mark is a 10px box in a 16px cell, so `inset: 0` cannot
       place it — an absolutely positioned box with a specified width ignores
       the opposite offset and lands flush against the near edge. Centred off
       its own dimensions instead, so retuning --state-dot-size moves
       nothing. */
    ".dsh-lead .dsh-mark {",
    "  inset: auto;",
    "  left: 50%;",
    "  top: 50%;",
    "  transform: translate(-50%, -50%);",
    "}",
    /* Hidden until the row is hovered or open — the crossfade's other half.
       Both halves are declared here so neither can be forgotten. */
    ".dsh-lead .dsh-chevron { opacity: 0; }",
    ".dsh-lead .dsh-chevron svg {",
    "  width: 10px;",
    "  height: 10px;",
    "  fill: var(--text-ter, #8b93a7);",
    "  transition: transform 160ms ease;",
    "}",
    ".accordion-header:hover .dsh-has-chevron .dsh-glyph { opacity: 0; }",
    ".accordion-header:hover .dsh-has-chevron .dsh-chevron { opacity: 1; }",
    '.accordion-header[aria-expanded="true"] .dsh-glyph { opacity: 0; }',
    '.accordion-header[aria-expanded="true"] .dsh-chevron { opacity: 1; }',
    '.accordion-header[aria-expanded="true"] .dsh-chevron svg {',
    "  transform: rotate(90deg);",
    "}",
    /* One size for both runs, one gray step apart. The label names a
       category and the subject names a thing, and a reader scanning a
       column needs to tell them apart without either one shouting. */
    ".dsh-row .dsh-label {",
    "  flex: none;",
    "  margin: 0;",
    "  color: var(--text-sec, #6a7186);",
    "  white-space: nowrap;",
    "}",
    ".dsh-row .dsh-subject {",
    "  flex: 1 1 auto;",
    "  min-width: 0;",
    "  margin: 0;",
    "  color: var(--text-ter, #8b93a7);",
    "  white-space: nowrap;",
    "  overflow: hidden;",
    "  text-overflow: ellipsis;",
    "}",
    ".dsh-row .dsh-suffix {",
    "  flex: none;",
    "  margin: 0 0 0 8px;",
    "  color: var(--text-ter, #8b93a7);",
    "  opacity: 0.75;",
    "  white-space: nowrap;",
    "}",
    /* 2x2 with a 1px radius: a square this small reads as a dot and a
       circle this small reads as noise, so it is drawn as the former and
       rounded just enough to lose its corners. */
    ".dsh-row .dsh-sep {",
    "  flex: none;",
    "  width: 2px;",
    "  height: 2px;",
    "  margin: 0 8px;",
    "  border-radius: 1px;",
    "  background: var(--text-ter, #8b93a7);",
    "  opacity: 0.6;",
    "}",
    /* Open, the subject is redundant with the body underneath it — unless
       the caller says otherwise, which tools do: a command is what the
       output is OF, so it stays on the row while the output is read.
       The separator goes with it; a dot between a label and nothing is a
       dot pointing at the row's own margin. */
    '.accordion-header[aria-expanded="true"] .dsh-hide-open .dsh-subject,',
    '.accordion-header[aria-expanded="true"] .dsh-hide-open .dsh-sep {',
    "  display: none;",
    "}",
  ].join("\n");

  static $styles = [
    css`
      :host {
        display: block;
        width: 100%;
        min-width: 0;
        /* How far the hover wash reaches past the row's own box. One
           length rather than a written-out inset, so a caller retunes the
           bleed in one place. */
        --dsh-row-bleed-x: 6px;
      }
      #root {
        position: relative;
        width: 100%;
        min-width: 0;
      }
      /* The accordion's own defaults are a page section's: 12px of header
         padding, an icon column sized for a heading, 36px of content
         indent. A transcript row is a denser object, and this block is the
         whole of that difference.

         The icon column goes to ZERO rather than being styled, because this
         row draws its own leading box — an accordion chevron beside a dsh
         chevron is two disclosure marks on one row. */
      #row {
        --accordion-header-padding: 0px;
        --accordion-header-gap: 0px;
        --accordion-icon-size: 0px;
        --accordion-icon-svg-size: 0px;
        --accordion-content-padding: 0px;
        --accordion-header-hover-inset: 0 calc(-1 * var(--dsh-row-bleed-x));
      }

      /* ---- running: the glare ------------------------------------- */
      /* A band gliding left to right across the row, and NOT a mark that
         changes shape. The gutter already carries a glyph the reader has
         learned; swapping it for a spinner mid-flight makes a column of
         running rows shimmer at the one x the eye uses as its edge.

         Drawn as a background POSITION rather than a translated child, so
         the band travels the row's real width whatever that turns out to
         be — a 300px child translated by a percentage travels 300px. */
      #glare {
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        height: 24px;
        pointer-events: none;
        opacity: 0;
        background-repeat: no-repeat;
        background-size: 300px 100%;
        background-position: -300px 0;
        background-image: linear-gradient(
          90deg,
          transparent 0%,
          color-mix(
            in srgb,
            var(--bg-hover, color-mix(in srgb, var(--text-pri, #1f2328) 6%, transparent)) 60%,
            transparent
          ) 50%,
          transparent 100%
        );
      }
      :host([state="running"]) #glare {
        opacity: 1;
        animation: dsh-row-glare 2.6s ease-out infinite;
      }
      @keyframes dsh-row-glare {
        from {
          background-position: -300px 0;
        }
        to {
          background-position: calc(100% + 300px) 0;
        }
      }
      /* Nothing replaces it. Unlike the state dot, the glare carries no
         fact the row does not also carry in words — a running tool row
         says its status in its label and its lack of a note — so the
         honest reduced-motion answer is an empty box, not a frozen band
         that reads as a rendering artefact. */
      @media (prefers-reduced-motion: reduce) {
        :host([state="running"]) #glare {
          animation: none;
          opacity: 0;
        }
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="root">
      <std-accordion id="row"></std-accordion>
      <div id="glare"></div>
    </div>
  `;
}

export namespace DshTurnRow {
  /** What the row's gutter says this row IS.
   *
   * Text glyphs, because this tree ships no icon set and a row drawn from
   * five inline SVGs is five assets to keep in step. They are chosen for
   * even optical weight at one size rather than for pictorial accuracy — a
   * transcript's gutter is a column, and a column of characters that differ
   * in weight has no edge to run the eye down.
   *
   * Exported so the slot components — and part B's ask, plan and attachment
   * — name a kind rather than paste a character. */
  export type Kind =
    | "thought"
    | "tool"
    | "search"
    | "read"
    | "edit"
    | "attachment"
    | "plan"
    | "ask";

  export const GLYPH: Record<Kind, string> = {
    thought: "✻",
    tool: "⏺",
    search: "⌕",
    read: "▸",
    edit: "±",
    attachment: "⎘",
    plan: "☰",
    ask: "?",
  };

  /** Four, and they are not four colours. `idle` and `running` share the
   * glyph and differ by the glare; `error` and `warn` replace the glyph with
   * `std-state-dot` in the matching state. A skin needing a fifth reading
   * says it in the label, which is already a string. */
  export type State = "idle" | "running" | "error" | "warn";

  /** How far the disclosed body hangs in. `prose` lands on the row's own
   * word column; `card` clears the row's left edge by a hair and no more,
   * because a bordered box indented to a text column reads as hanging off
   * the row rather than under it. */
  export type BodyIndent = "prose" | "card";

  export type Props = {
    kind: Kind;
    /** The category, in the leading gray step. One word where possible. */
    label: string;
    /** The thing acted on. An ELEMENT rather than a string when the caller
     * needs to reach it after the row is built — the thought's ticker sets
     * `scrollLeft` on this every bind, which it cannot do to a string. */
    subject: string | HTMLElement;
    /** A trailing fact, dimmer again. */
    suffix?: Option<string>;
    state?: Option<State>;
    /** The disclosed body, or null for a row that does not open: no
     * chevron, no click, no focus stop, no hover light. */
    body?: Option<HTMLElement>;
    bodyIndent?: Option<BodyIndent>;
    /** Forces the disclosure open. Collapsed is always the default — a
     * transcript is skimmed far more often than it is audited. */
    expanded?: Option<boolean>;
    /** Keeps the inline subject on the row while the body is open. True for
     * a tool, whose subject is the command the body is the output OF; false
     * for a body that simply restates it. */
    keepSubjectWhenOpen?: Option<boolean>;
    /** The row is a CONTROL despite having no disclosure — an attachment
     * that opens on activation. Restores the pointer cursor the accordion's
     * inert header removes; the caller wires the actual handler on its own
     * host. Meaningless (and ignored) when a body is present. */
    interactive?: Option<boolean>;
  };
}
